import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox } from "./shared";
import { LifecycleTabs } from "@/components/lifecycle/LifecycleTabs";
import { ArchivedPanel } from "@/components/lifecycle/ArchivedPanel";
import { AiSourceBadge } from "@/components/AiSourceBadge";
import { FileText, Copy, Pencil, History, Check, Send, X, Ban, Archive, Link2 } from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-white/10 text-gray-300 border-white/20",
  sent: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  accepted: "bg-green-500/15 text-green-300 border-green-500/30",
  rejected: "bg-red-500/15 text-red-300 border-red-500/30",
};
const StatusPill = ({ status }) => (
  <span data-testid="proposal-status" className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border rounded-full ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>{status || "draft"}</span>
);

const FILTERS = ["all", "draft", "sent", "accepted", "rejected"];

export function Proposals() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [lifeTab, setLifeTab] = useState("active");

  const load = () => api.get("/proposals").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter((p) => {
      if (filter !== "all" && (p.status || "draft") !== filter) return false;
      if (q && !`${p.title} ${p.organization} ${p.recommended_package}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [items, filter, q]);

  if (!items) return <Loader />;
  return (
    <div>
      <Head title="Proposals" desc="Generated client proposals — edit, track status, export, and view version history." />
      <div className="mb-5"><LifecycleTabs module="proposals" value={lifeTab} onChange={setLifeTab} refreshKey={(items || []).length} /></div>
      {lifeTab !== "active" ? <ArchivedPanel module="proposals" status={lifeTab} singular="Proposal" onChanged={load} /> : (<>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex gap-1 bg-navy-800 border border-white/10 p-1" data-testid="proposal-filters">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`proposal-filter-${f}`}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${filter === f ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>
              {f}{f !== "all" ? ` (${items.filter((p) => (p.status || "draft") === f).length})` : ` (${items.length})`}
            </button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search proposals…" data-testid="proposal-search"
          className="bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white focus:border-gold focus:outline-none flex-1 min-w-[180px]" />
      </div>

      {filtered.length === 0 ? <EmptyBox text={q || filter !== "all" ? "No proposals match your filters." : "No proposals yet. Generate one from a client request (Leads)."} /> : (
        <div className="space-y-3">
          {filtered.map((p) => (
            <ProposalCard key={p.id} p={p} open={open === p.id} onToggle={() => setOpen(open === p.id ? null : p.id)} onChanged={load} />
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}

function ProposalCard({ p, open, onToggle, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState(null);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/proposals/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${(p.organization || p.title || "ATF").replace(/\s+/g, "-")}-proposal.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF"); }
  };

  const copyText = () => {
    const label = p.ai_mode === "live" ? "AI Drafted · Live GPT" : p.ai_mode === "fallback" ? "Auto Generated · Offline Engine" : "Created Manually";
    navigator.clipboard.writeText(`${label}\n\n${p.title}\n\n${p.proposal_text}`).then(() => toast.success("Copied with AI attribution.")).catch(() => toast.error("Copy failed"));
  };

  const copyLink = () => {
    const url = `${window.location.origin}/proposal/${p.share_token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Public proposal link copied.")).catch(() => toast.error("Copy failed"));
  };

  const startEdit = () => { setForm({ proposal_text: p.proposal_text, recommended_package: p.recommended_package, price_range: p.price_range, timeline: p.timeline }); setEditing(true); };
  const saveEdit = async () => {
    setBusy(true);
    try { await api.patch(`/proposals/${p.id}`, form); toast.success("Proposal updated. A version snapshot was saved."); setEditing(false); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); } finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try { await api.post(`/proposals/${p.id}/status`, { status }); toast.success(`Marked ${status}. Client notified.`); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Status update failed."); } finally { setBusy(false); }
  };

  const archive = async () => {
    if (!window.confirm(`Archive "${p.title}"? It moves to the Records Center where it can be restored or permanently deleted.`)) return;
    setBusy(true);
    try { await api.post(`/lifecycle/proposals/${p.id}/archive`, { reason: "" }); toast.success("Proposal archived."); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Archive failed."); } finally { setBusy(false); }
  };

  const loadVersions = async () => {
    setShowVersions((v) => !v);
    if (versions === null) {
      try { const r = await api.get(`/proposals/${p.id}/versions`); setVersions(r.data); } catch { setVersions([]); }
    }
  };

  return (
    <div className="bg-navy-800 border border-white/5 p-5" data-testid={`proposal-${p.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-medium truncate">{p.title}</span>
            <StatusPill status={p.status} />
            <AiSourceBadge mode={p.ai_mode} size="xs" />
          </div>
          <div className="text-gray-500 text-sm">{p.organization} · {p.recommended_package}{p.price_range ? ` · ${p.price_range}` : ""}</div>
          <div className="text-[11px] text-gray-500 mt-0.5" data-testid={`proposal-cost-${p.id}`}>
            Generation cost: {p.ai_mode === "live"
              ? `~$${((Math.max(1, Math.round((p.proposal_text || "").length / 4)) / 1_000_000) * 5).toFixed(4)} · AI-written`
              : "$0.00 · deterministic (no AI call)"}
          </div>
        </div>
        <button onClick={onToggle} data-testid={`proposal-toggle-${p.id}`} className="text-gold text-sm">{open ? "Hide" : "View"}</button>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex flex-wrap justify-end gap-2 mb-4" data-testid={`proposal-actions-${p.id}`}>
            {!editing && <ActBtn icon={Pencil} label="Edit" testid={`proposal-edit-${p.id}`} onClick={startEdit} />}
            <ActBtn icon={FileText} label="PDF" testid={`proposal-pdf-${p.id}`} onClick={downloadPdf} />
            <ActBtn icon={Copy} label="Copy" testid={`proposal-copy-${p.id}`} onClick={copyText} />
            {p.share_token && <ActBtn icon={Link2} label="Copy link" testid={`proposal-copy-link-${p.id}`} onClick={copyLink} accent="green" />}
            <ActBtn icon={History} label="Versions" testid={`proposal-versions-${p.id}`} onClick={loadVersions} />
            <div className="w-px bg-white/10 mx-1" />
            <ActBtn icon={Send} label="Sent" testid={`proposal-mark-sent-${p.id}`} onClick={() => setStatus("sent")} disabled={busy} />
            <ActBtn icon={Check} label="Accepted" testid={`proposal-mark-accepted-${p.id}`} onClick={() => setStatus("accepted")} disabled={busy} accent="green" />
            <ActBtn icon={Ban} label="Rejected" testid={`proposal-mark-rejected-${p.id}`} onClick={() => setStatus("rejected")} disabled={busy} accent="red" />
            <div className="w-px bg-white/10 mx-1" />
            <ActBtn icon={Archive} label="Archive" testid={`proposal-archive-${p.id}`} onClick={archive} disabled={busy} accent="red" />
          </div>

          {editing ? (
            <div className="space-y-2" data-testid={`proposal-edit-form-${p.id}`}>
              <div className="grid sm:grid-cols-3 gap-2">
                <input className="bg-navy border border-white/10 px-3 py-2 text-sm text-white" placeholder="Package" value={form.recommended_package || ""} onChange={(e) => setForm({ ...form, recommended_package: e.target.value })} data-testid={`proposal-edit-package-${p.id}`} />
                <input className="bg-navy border border-white/10 px-3 py-2 text-sm text-white" placeholder="Price range" value={form.price_range || ""} onChange={(e) => setForm({ ...form, price_range: e.target.value })} data-testid={`proposal-edit-price-${p.id}`} />
                <input className="bg-navy border border-white/10 px-3 py-2 text-sm text-white" placeholder="Timeline" value={form.timeline || ""} onChange={(e) => setForm({ ...form, timeline: e.target.value })} data-testid={`proposal-edit-timeline-${p.id}`} />
              </div>
              <textarea rows={12} className="w-full bg-navy border border-white/10 px-3 py-2 text-sm text-gray-200 font-body leading-relaxed" value={form.proposal_text || ""} onChange={(e) => setForm({ ...form, proposal_text: e.target.value })} data-testid={`proposal-edit-text-${p.id}`} />
              <div className="flex gap-2">
                <button onClick={saveEdit} disabled={busy} data-testid={`proposal-save-${p.id}`} className="bg-gold text-navy px-5 py-2 text-sm font-semibold hover:bg-gold-light disabled:opacity-60">{busy ? "Saving…" : "Save changes"}</button>
                <button onClick={() => setEditing(false)} className="border border-white/15 text-gray-300 px-4 py-2 text-sm hover:bg-white/5">Cancel</button>
              </div>
            </div>
          ) : (
            <pre className="whitespace-pre-wrap font-body text-gray-300 text-sm leading-relaxed">{p.proposal_text}</pre>
          )}

          {showVersions && (
            <div className="mt-4 pt-4 border-t border-white/10" data-testid={`proposal-version-list-${p.id}`}>
              <h4 className="text-white text-sm font-medium mb-2 flex items-center gap-2"><History size={14} className="text-gold" /> Version history</h4>
              {versions === null ? <p className="text-gray-500 text-sm">Loading…</p>
                : versions.length === 0 ? <p className="text-gray-500 text-sm">No prior versions — edits will be snapshotted here.</p>
                : versions.map((v) => (
                  <details key={v.id} className="bg-navy border border-white/10 p-3 mb-2" data-testid={`proposal-version-${v.version}`}>
                    <summary className="cursor-pointer text-gray-300 text-sm flex items-center justify-between">
                      <span>v{v.version} · {v.editor_name || "system"} · {v.recommended_package}</span>
                      <span className="text-gray-600 text-xs">{v.snapshot_at ? new Date(v.snapshot_at).toLocaleString() : ""}</span>
                    </summary>
                    <pre className="whitespace-pre-wrap font-body text-gray-400 text-xs leading-relaxed mt-2">{v.proposal_text}</pre>
                  </details>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ActBtn = ({ icon: Icon, label, onClick, testid, disabled, accent }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid} title={label}
    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors disabled:opacity-40 ${
      accent === "green" ? "border-green-500/30 text-green-300 hover:bg-green-500/10"
      : accent === "red" ? "border-red-500/30 text-red-300 hover:bg-red-500/10"
      : "border-white/10 text-gray-200 hover:border-gold/50 hover:text-white hover:bg-white/5"}`}>
    <Icon size={13} className={accent ? "" : "text-gold"} /> {label}
  </button>
);
